import React from 'react';
import { useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useDidMount } from 'src/hooks';
import { useTranslation } from 'react-i18next';
import paramSelectors from 'src/selectors/parameters';
import SmConfigTextField from './SmConfigTextField';
import SmConfigImageField from './SmConfigImageField';
import TimeInput from '../common/timeInput/TimeInput';
import CustomCard from '../common/Card';
import parametersCfg from 'src/reducers/utils/parameters';
import Loader from '../common/Loader';

const SmartMessagingConfig = ({
  onSmConfigFieldChange,
  imageMaxWeight,
  defaultSmsHours,
  invalidities,
  clubId,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const dispatch = useDispatch();

  const smartmParams = useSelector(paramSelectors.getCurrentParams);
  useDidMount(() => {
    dispatch({ type: 'SET_CURRENT_PARAMS', value: clubId });
  }, [clubId, dispatch]);
  if (!smartmParams)
    return (
      <div>
        <Loader wrapperClass="ghostWrapperClass" text={t('smartmessaging.loading')} />
      </div>
    );
  return (
    <div
      style={{
        flex: 1,
        overflow: 'auto',
        padding: '8px',
      }}
    >
      <div
        style={{
          overflow: 'auto',
          width: 'max-content',
        }}
      >
        <CustomCard title={t(`smartmessaging.config.sectionLabel.smsHours`, { defaultSmsHours })}>
          <div
            style={{
              marginTop: '8px',
              marginBottom: '8px',
              textAlign: 'left',
              padding: '0 24px',
              overflow: 'hidden',
              gridGap: '16px 24px',
              display: 'grid',
              // could come from parametersCfg using parametersCfg.textParamKeys
              gridTemplateAreas: '"openingtime closingtime"',
              justifyContent: 'start',
              gridTemplateColumns: '480px 480px',
            }}
          >
            {parametersCfg.timeParamKeys.map(timeParamKey => (
              <TimeInput
                initTime={smartmParams[timeParamKey]}
                onTimeChange={value => {
                  onSmConfigFieldChange(value, timeParamKey);
                }}
                validity={
                  invalidities[timeParamKey] || { key: timeParamKey, isValid: true, messages: [] }
                }
                key={timeParamKey}
                paramKey={timeParamKey}
                label={t(`smartmessaging.config.field.${timeParamKey}`)}
              />
            ))}
          </div>
        </CustomCard>
        <CustomCard title={t(`smartmessaging.config.sectionLabel.contentData`)}>
          <div
            style={{
              marginBottom: '8px',
              textAlign: 'left',
              padding: '0 24px',
              overflow: 'hidden',
              gridGap: '16px 24px',
              display: 'grid',
              // could come from parametersCfg using parametersCfg.textParamKeys
              gridTemplateAreas:
                '"smsSender googleplayUrl" \n' +
                '"emailSender appstoreUrl" \n' +
                '"resamaniaUrl instagramUrl" \n' +
                '"bookingUrl facebookUrl" \n' +
                '"salelUrl twitterUrl" \n' +
                '"unsubscribeUrl youtubeUrl" \n' +
                '"passwordforgottenUrl googlePlusUrl" \n' +
                '"clubUrl contactCenterMail" \n' +
                '"0 contactCenterPhone"',
              justifyContent: 'start',
              gridTemplateColumns: '480px 480px',
            }}
          >
            {parametersCfg.textParamKeys.map(textParamKey => (
              <SmConfigTextField
                onChange={value => {
                  onSmConfigFieldChange(value, textParamKey);
                }}
                paramKey={textParamKey}
                key={textParamKey}
                value={smartmParams[textParamKey] || ''}
                label={t(`smartmessaging.config.field.${textParamKey}`)}
                validity={
                  invalidities[textParamKey] || { key: textParamKey, isValid: true, messages: [] }
                }
              />
            ))}
          </div>
        </CustomCard>
        <CustomCard title={t(`smartmessaging.config.sectionLabel.headerFooter`)}>
          <SmConfigImageField
            label={
              <div style={{ textAlign: 'left', marginBottom: '8px' }}>
                <span style={{ marginRight: '8px' }}>
                  {t('smartmessaging.config.field.headerStoredfile')}
                </span>
                <span style={{ fontWeight: 400, color: theme.palette.text.secondary }}>
                  {t('smartmessaging.config.field.headerAndFooterAdvice', { imageMaxWeight })}
                </span>
              </div>
            }
            paramKey="headerStoredfileId"
            storedFileId={
              (smartmParams.headerStoredfileId && Number(smartmParams.headerStoredfileId)) || null
            }
          />
          <SmConfigImageField
            label={
              <div style={{ textAlign: 'left', marginBottom: '8px' }}>
                <span style={{ marginRight: '8px' }}>
                  {t('smartmessaging.config.field.footerStoredfile')}
                </span>
                <span style={{ fontWeight: 400, color: theme.palette.text.secondary }}>
                  {t('smartmessaging.config.field.headerAndFooterAdvice', { imageMaxWeight })}
                </span>
              </div>
            }
            paramKey="footerStoredfileId"
            storedFileId={
              (smartmParams.footerStoredfileId && Number(smartmParams.footerStoredfileId)) || null
            }
          />
        </CustomCard>
      </div>
    </div>
  );
};

SmartMessagingConfig.propTypes = {
  onSmConfigFieldChange: PropTypes.func.isRequired,
  imageMaxWeight: PropTypes.string.isRequired,
  defaultSmsHours: PropTypes.string.isRequired,
  invalidities: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  invalidities: state.parameters.invalidities,
  imageMaxWeight: state.app.imageMaxSize,
  defaultSmsHours: state.app.config.defaultSmsHours,
});

const actionCreators = {
  onSmConfigFieldChange: (value, textParamKey) => ({
    type: 'ON_SM_CFG_FIELD_CHANGE',
    value: { value, key: textParamKey },
  }),
};

export default connect(mapStateToProps, actionCreators)(SmartMessagingConfig);
